import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DataSubjectSearchResult, ErasurePreview } from '@cloudsign/shared';
import type { DataSubject, DataSubjectHit } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Alert, Button, Card, CheckboxField, EmptyState, FilterBar, TableWrap, TextField, Th } from '../../components/ui';
import { ErrorLine } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { buildSubject, hitKey, isEmptySubject, jobsKey, postJob } from './api';
import { ErasePanel } from './ErasePanel';

/** Find a person across every module, tick their records, then export, erase or place a hold. */
export function FindTab({ onJob, onHold }: { onJob: (jobId: string) => void; onHold: (subject: DataSubject) => void }) {
  const canExport = useCan('data_protection.export');
  const canErase = useCan('data_protection.erase');
  const canHold = useCan('data_protection.legal_hold');
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [useName, setUseName] = useState(false);
  const [ticked, setTicked] = useState<Record<string, DataSubjectHit>>({});
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const [preview, setPreview] = useState<{ subject: DataSubject; data: ErasurePreview } | null>(null);

  const search = useMutation({
    mutationFn: () => {
      const body: Record<string, string> = {};
      if (form.name.trim().length >= 2) body['name'] = form.name.trim();
      if (form.email.trim()) body['email'] = form.email.trim();
      if (form.phone.trim()) body['phone'] = form.phone.trim();
      return api.post('/data-protection/search', { body, schema: DataSubjectSearchResult });
    },
    onSuccess: () => {
      setTicked({});
      setPreview(null);
    },
  });
  const canSearch = form.name.trim().length >= 2 || form.email.trim() !== '' || form.phone.trim() !== '';
  const hits = search.data?.hits ?? [];
  const chosen = hits.filter((h) => ticked[hitKey(h)]);
  const subject = buildSubject(chosen, { email: form.email, phone: form.phone, name: useName ? form.name : undefined });
  const hasSubject = !isEmptySubject(subject);

  const exportJob = useMutation({
    mutationFn: () => postJob('/data-protection/exports', { subject, reason: reason.trim(), requestId: null }),
    onSuccess: (job) => {
      void qc.invalidateQueries({ queryKey: jobsKey });
      toast(t('p4dp.find.exportStarted'));
      onJob(job.id);
    },
  });
  const previewErase = useMutation({
    mutationFn: () => api.post('/data-protection/erasures/preview', { body: { subject }, schema: ErasurePreview }),
    onSuccess: (data) => setPreview({ subject, data }),
  });

  const needReason = (go: () => void) => {
    if (reason.trim() === '') {
      setReasonError(true);
      return;
    }
    setReasonError(false);
    go();
  };

  const toggle = (h: DataSubjectHit, on: boolean) => {
    setPreview(null);
    setTicked((cur) => {
      const next = { ...cur };
      if (on) next[hitKey(h)] = h;
      else delete next[hitKey(h)];
      return next;
    });
  };
  const allTicked = hits.length > 0 && chosen.length === hits.length;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (canSearch) search.mutate();
  };

  return (
    <div className="space-y-4">
      <Card title={t('p4dp.find.title')} description={t('p4dp.find.help')}>
        <form onSubmit={onSubmit} role="search" aria-label={t('p4dp.find.title')}>
          <FilterBar columns="sm:grid-cols-3">
            <TextField label={t('p4dp.find.name')} value={form.name} maxLength={120} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField label={t('p4dp.find.email')} type="email" value={form.email} maxLength={254} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <TextField label={t('p4dp.find.phone')} type="tel" value={form.phone} maxLength={32} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </FilterBar>
          {form.name.trim().length >= 2 ? <CheckboxField label={t('p4dp.find.useName')} checked={useName} onChange={(e) => setUseName(e.target.checked)} /> : null}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button variant="primary" type="submit" disabled={!canSearch || search.isPending}>
              {t('p4dp.find.search')}
            </Button>
            {!canSearch ? <span className="text-sm text-slate-600">{t('p4dp.find.needOne')}</span> : null}
          </div>
          <ErrorLine error={search.error} />
        </form>
      </Card>

      {search.data ? (
        <Card title={t('p4dp.find.results')}>
          {search.data.truncated ? <Alert kind="info">{t('p4dp.find.truncated')}</Alert> : null}
          {hits.length === 0 ? (
            <EmptyState message={t('p4dp.find.none')} />
          ) : (
            <TableWrap caption={t('p4dp.find.results')}>
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <Th>
                    <input
                      type="checkbox"
                      aria-label={t('p4dp.find.selectAll')}
                      checked={allTicked}
                      onChange={(e) => {
                        setPreview(null);
                        setTicked(e.target.checked ? Object.fromEntries(hits.map((h) => [hitKey(h), h])) : {});
                      }}
                    />
                  </Th>
                  <Th>{t('p4dp.find.col.record')}</Th>
                  <Th>{t('p4dp.find.col.module')}</Th>
                  <Th>{t('p4dp.find.col.when')}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {hits.map((h) => (
                  <tr key={hitKey(h)}>
                    <td className="px-4 py-2.5">
                      <input type="checkbox" aria-label={t('p4dp.find.tick', { label: h.label })} checked={Boolean(ticked[hitKey(h)])} onChange={(e) => toggle(h, e.target.checked)} />
                    </td>
                    <td className="px-4 py-2.5 font-medium">{h.label}</td>
                    <td className="px-4 py-2.5">{h.module}</td>
                    <td className="px-4 py-2.5">{formatDateTime(h.at)}</td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Card>
      ) : null}

      {hits.length > 0 ? (
        <Card>
          <p className="mb-3 text-sm text-slate-600" aria-live="polite">
            {t('p4dp.find.selected', { count: chosen.length })}
          </p>
          <TextField
            label={t('p4dp.find.reason')}
            hint={t('p4dp.find.reasonHint')}
            value={reason}
            maxLength={500}
            error={reasonError && reason.trim() === '' ? t('p4dp.find.reasonNeeded') : undefined}
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="mt-4 flex flex-wrap gap-2">
            {canExport ? (
              <Button variant="primary" disabled={!hasSubject || exportJob.isPending} onClick={() => needReason(() => exportJob.mutate())}>
                {t('p4dp.find.export')}
              </Button>
            ) : null}
            {canErase ? (
              <Button variant="danger" disabled={!hasSubject || previewErase.isPending} onClick={() => needReason(() => previewErase.mutate())}>
                {t('p4dp.find.erase')}
              </Button>
            ) : null}
            {canHold ? (
              <Button disabled={subject.memberIds.length + subject.visitIds.length === 0} onClick={() => onHold(subject)}>
                {t('p4dp.find.hold')}
              </Button>
            ) : null}
          </div>
          <ErrorLine error={exportJob.error ?? previewErase.error} />
        </Card>
      ) : null}

      {preview ? (
        <ErasePanel
          subject={preview.subject}
          preview={preview.data}
          reason={reason.trim()}
          onCancel={() => setPreview(null)}
          onStarted={(job) => {
            setPreview(null);
            onJob(job.id);
          }}
        />
      ) : null}
    </div>
  );
}
